import { useState, useMemo, useEffect, Fragment } from "react";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import SearchIcon from "../../../components/Assests/Search Icon.png";
import { formatDate } from "../../../utility/Formatdate";
import Button from "../../../components/atoms/button/Button";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getSampleData,
  scanSample,
  markSampleAsRejected,
  markSampleAsReceived,
} from "./scanSampleApi";
import { printSamplesPDF } from "../../Lab Admin Tabs/ScanSample/scanSampleApi";
import SecondTab from "../../../components/organisms/secondTab";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const apiToTableRow = (item) => ({
  "Group ID": parseInt(item.groupId) || item.groupId,
  "Sample ID": item.sampleId,
  Date: formatDate(item.date),
  "Barcode Number": item.barCodeNumber,
  "Species Type": item.speciesType,
  "Product Type": item.productType,
  "Lot Number": item.lotNumber,
  "Company Name": item.companyName,
  Status: item.status,
  id: item.sampleId,
});

const ScanType = {
  None: 0,
  Barcode: 1,
  GroupId: 2,
  SampleId: 3,
};

const ScanSample = ({ TabConfig }) => {
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [sortBy, setSortBy] = useState();
  const [sortDir, setSortDir] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalSelectedRows, setModalSelectedRows] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false); // Popup reject
  const [showMainRejectConfirm, setShowMainRejectConfirm] = useState(false); // Main table reject
  const [scanType, setScanType] = useState(ScanType.Barcode);
  const [paginationData, setPaginationData] = useState({});

  useEffect(() => {
    fetchReceivedSamples(searchValue);
    // eslint-disable-next-line
  }, []);

  const fetchReceivedSamples = async (
    filterText = "",
    currentPage,
    rowsPerPage
  ) => {
    try {
      setLoading(true);
      const res = await getSampleData(filterText, currentPage, rowsPerPage);
      if (res.data) {
        const { totalRecords, pageNumber, pageSize, totalPages } = res.data;
        setData((res.data.results || []).map(apiToTableRow));
        setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      }
    } catch (err) {
      console.error("Failed to fetch samples", err);
    } finally {
      setLoading(false);
    }
  };

  const handleScanClick = async () => {
    if (!barcodeInput) return;
    try {
      setLoading(true);
      const response = await scanSample(scanType, barcodeInput, 1, 10);
      if (response.data && response.data.length > 0) {
        setModalData(
          response.data.map((item) => ({
            "Sample ID": item.sampleId,
            "Barcode No.": item.barCodeNumber,
            "Species Type": item.speciesType,
            "Product Type": item.productType,
            "Lot Number": item.lotNumber,
            "Company Name": item.companyName,
            Status: item.status,
          }))
        );
        setModalSelectedRows([]);
        setShowModal(true);
      } else {
        toast.warning("No samples found for the given input");
      }
    } catch (error) {
      console.error("Error scanning sample:", error);
    } finally {
      setLoading(false);
      setBarcodeInput("");
    }
  };

  const handleSearch = () => {
    fetchReceivedSamples(searchValue.trim());
  };

  const handleReject = (main = false) => {
    const count = main ? selectedRows.length : modalSelectedRows.length;
    if (!count) {
      toast.warning("Please select at least one sample to remove");
      return;
    }
    main ? setShowMainRejectConfirm(true) : setShowConfirmDialog(true);
  };

  const handleConfirmReject = async (main = false) => {
    try {
      const ids = main
        ? selectedRows.map((i) => sortedData[i]["Sample ID"]).filter(Boolean)
        : modalSelectedRows
            .map((i) => modalData[i]["Sample ID"])
            .filter(Boolean);

      if (!ids.length) {
        alert("No valid sample IDs found");
        return;
      }

      await markSampleAsRejected(ids);
      await fetchReceivedSamples(searchValue.trim());

      if (main) {
        setSelectedRows([]);
        setShowMainRejectConfirm(false);
      } else {
        setModalSelectedRows([]);
        setShowModal(false);
        setShowConfirmDialog(false);
      }
      toast.success("Selected samples have been remove successfully");
    } catch (err) {
      console.error("Reject failed:", err);
      toast.error("Failed to remove samples. Please try again.");
    }
  };

  const handleReceive = async () => {
    if (!modalSelectedRows.length) {
      toast.warning("Please select at least one sample to receive");
      return;
    }

    try {
      const ids = modalSelectedRows
        .map((idx) => modalData[idx]["Sample ID"])
        .filter(Boolean);

      if (!ids.length) {
        alert("No valid sample IDs found");
        return;
      }

      await markSampleAsReceived(ids);
      await fetchReceivedSamples(searchValue.trim());
      setModalSelectedRows([]);
      setShowModal(false);
      toast.success("Selected samples have been received successfully");
    } catch (err) {
      console.error("Receive failed:", err);
      toast.error("Failed to receive samples. Please try again.");
    }
  };

  const handlePrint = async () => {
    if (!selectedRows.length) {
      toast.warning("Please select at least one sample to print");
      return;
    }
    try {
      setLoading(true);
      const selectedSamplesData = selectedRows.map((rowIndex) => {
        const row = sortedData[rowIndex];
        return {
          sampleId: row.id,
          groupId: row["Group ID"],
          companyName: row["Company Name"],
          species: row["Species Type"],
          productType: row["Product Type"],
          lotNo: row["Lot Number"],
          barcodeNumber: row["Barcode Number"],
          status: row.Status,
        };
      });

      await printSamplesPDF(
        selectedSamplesData,
        selectedSamplesData[0]?.groupId
      );
    } catch (err) {
      console.error("Print failed", err);
      toast.error("Failed to print. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortBy) return data;
    return [...data].sort((a, b) => {
      let A = a[sortBy] || "";
      let B = b[sortBy] || "";
      A = A.toString().toLowerCase();
      B = B.toString().toLowerCase();
      if (A === B) return 0;
      return A < B ? (sortDir === "asc" ? -1 : 1) : sortDir === "asc" ? 1 : -1;
    });
  }, [data, sortBy, sortDir]);

  const toggleRow = (idx, setter) =>
    setter((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );

  const toggleAll = (isModal = false) => {
    if (isModal) {
      const all = modalSelectedRows.length === modalData.length;
      setModalSelectedRows(all ? [] : modalData.map((_, i) => i));
    } else {
      const pageIndices = sortedData.map((_, i) => i);
      const all =
        selectedRows.length === sortedData.length && sortedData.length;
      setSelectedRows(all ? [] : pageIndices);
    }
  };

  const mainCols = [
    { header: "Group ID", accessor: "Group ID" },
    { header: "Sample ID", accessor: "Sample ID" },
    { header: "Date", accessor: "Date" },
    { header: "Barcode Number", accessor: "Barcode Number" },
    { header: "Species", accessor: "Species Type" },
    { header: "Product", accessor: "Product Type" },
    { header: "Lot Number", accessor: "Lot Number" },
    { header: "Company", accessor: "Company Name" },
    { header: "Status", accessor: "Status" },
  ];

  const modalCols = [
    { header: "Sample ID", accessor: "Sample ID" },
    { header: "Barcode No.", accessor: "Barcode No." },
    { header: "Species", accessor: "Species Type" },
    { header: "Product", accessor: "Product Type" },
    { header: "Lot Number", accessor: "Lot Number" },
    { header: "Company", accessor: "Company Name" },
    { header: "Status", accessor: "Status" },
  ];

  const sortHeader = (c) => (
    <span
      style={{ cursor: "pointer" }}
      onClick={() => {
        setSortDir(sortBy === c ? (sortDir === "asc" ? "desc" : "asc") : "asc");
        setSortBy(c);
      }}
    >
      {c}
    </span>
  );

  const headerColumns = [
    {
      header: (
        <input
          type="checkbox"
          checked={
            selectedRows.length === sortedData.length && sortedData.length
          }
          onChange={() => toggleAll(false)}
        />
      ),
      accessor: "cb",
      disableSort: true,
    },
    ...mainCols.map((c) => ({
      ...c,
      header: sortHeader(c.header),
      disableSort: false,
    })),
  ];

  const modalHeaderColumns = [
    {
      header: (
        <input
          type="checkbox"
          checked={
            modalSelectedRows.length === modalData.length && modalData.length
          }
          onChange={() => toggleAll(true)}
        />
      ),
      accessor: "checkbox",
      disableSort: true,
    },
    ...modalCols.map((c) => ({
      ...c,
      disableSort: false,
    })),
  ];

  const renderRow = (row, idx) => {
    return (
      <tr key={idx}>
        <td>
          <input
            type="checkbox"
            checked={selectedRows.includes(idx)}
            onChange={() => toggleRow(idx, setSelectedRows)}
          />
        </td>
        {mainCols.map((c, i) => (
          <td key={i}>{row[c.accessor]}</td>
        ))}
      </tr>
    );
  };

  return (
    <Fragment>
      <SecondTab tabConfig={TabConfig} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <PageDescriptionBar items={[
        { type: "icon", icon: "🔍", text: "Scan Type – Choose barcode or manual scan mode" },
        { type: "icon", icon: "📷", text: "Group Samples – Batch selected samples into a single group" },
        { type: "separator" },
        { type: "icon", icon: "🖨️", text: "Print – Print labels for selected samples" },
        { type: "icon", icon: "❌", text: "Reject / Remove – Remove selected samples from the current batch" },
      ]} />
      <div className="container-fluid py-4">
        <div className="bg-white rounded shadow-sm p-3 mb-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select"
              style={{ width: "120px", height: "45px" }}
              value={scanType}
              onChange={(e) => setScanType(Number(e.target.value))}
            >
              <option value={ScanType.Barcode}>Barcode</option>
              <option value={ScanType.GroupId}>Group Id</option>
            </select>
            <input
              className="form-control"
              style={{
                width: "250px",
                height: "45px",
                marginLeft: "-8px",
                marginRight: "5px",
              }}
              placeholder={`Enter ${
                scanType === ScanType.Barcode
                  ? "Barcode"
                  : scanType === ScanType.GroupId
                  ? "Group ID"
                  : scanType === ScanType.SampleId
                  ? "Sample ID"
                  : ""
              }`}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleScanClick()}
            />
            <Button variant="outlined" onClick={handleScanClick}>
              Scan
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant={selectedRows.length ? "outlined" : "disabled"}
              disabled={selectedRows.length === 0}
              onClick={() => selectedRows.length && handleReject(true)}
            >
              Remove
            </Button>
            <Button
              variant={selectedRows.length ? "outlined" : "disabled"}
              disabled={selectedRows.length === 0 || loading}
              onClick={() => selectedRows.length && handlePrint()}
            >
              Print
            </Button>
            <div
              className="line-separator"
              style={{
                width: "1px",
                height: "46px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            />
            <div style={{ position: "relative" }}>
              <input
                className="form-control"
                style={{
                  width: "250px",
                  paddingRight: "40px",
                  borderLeft: "1px solid #ccc",
                  borderRadius: "5px",
                  borderLeftWidth: "1px",
                  borderLeftStyle: "solid",
                  borderLeftColor: "#ccc",
                }}
                placeholder="Search Barcode/Group ID"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <img
                src={SearchIcon}
                onClick={handleSearch}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                alt="Search"
              />
            </div>
          </div>
        </div>
        <div className="bg-white rounded shadow-sm p-4">
          <SimpleTable
            columns={headerColumns}
            data={sortedData}
            customRowRender={renderRow}
            showPagination
            loading={loading}
            paginationData={paginationData}
            searchValue={searchValue}
            fetchData={fetchReceivedSamples}
          />
          {/* POPUP GROUP SAMPLES MODAL */}
          <ReusableModal
            show={showModal}
            onHide={() => setShowModal(false)}
            title="Group Samples"
            size="xl"
            buttons={[
              {
                text: "Remove",
                variant: "outlined",
                onClick: () => handleReject(false),
              },
              { text: "Receive", variant: "active", onClick: handleReceive },
            ]}
          >
            <SimpleTable
              columns={modalHeaderColumns}
              data={modalData}
              customRowRender={(row, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="checkbox"
                      checked={modalSelectedRows.includes(idx)}
                      onChange={() => toggleRow(idx, setModalSelectedRows)}
                    />
                  </td>
                  {modalCols.map((c, i) => (
                    <td key={i}>{row[c.accessor]}</td>
                  ))}
                </tr>
              )}
              showPagination={false}
            />
          </ReusableModal>

          {/* POPUP REMOVE CONFIRMATION MODAL */}
          <ReusableModal
            show={showConfirmDialog}
            onHide={() => setShowConfirmDialog(false)}
            title="Confirm Remove"
            buttons={[
              {
                text: "Cancel",
                variant: "outlined",
                onClick: () => setShowConfirmDialog(false),
              },
              {
                text: "OK, Remove",
                variant: "active",
                onClick: () => handleConfirmReject(false),
              },
            ]}
          >
            <p>Remove {modalSelectedRows.length} selected sample(s)?</p>
          </ReusableModal>

          {/* MAIN TABLE REMOVE CONFIRMATION MODAL */}
          <ReusableModal
            show={showMainRejectConfirm}
            onHide={() => setShowMainRejectConfirm(false)}
            title="Confirm Remove"
            buttons={[
              {
                text: "Cancel",
                variant: "outlined",
                onClick: () => setShowMainRejectConfirm(false),
              },
              {
                text: "OK, Remove",
                variant: "active",
                onClick: () => handleConfirmReject(true),
              },
            ]}
          >
            <p>Remove {selectedRows.length} selected sample(s)?</p>
          </ReusableModal>
        </div>
      </div>
    </Fragment>
  );
};

export default ScanSample;
